import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContinueIcon from '@mui/icons-material/PlayArrow';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import { EnRouteEmergencyButtonsProps } from './types';
import { ArcButton, ArcStack, ArcText } from '@src/core/ui';
import { useSendCommandMutation } from '@src/features/order/redux';
import { EmergencyStatusEnum } from '@src/features/order/entities';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArcSocketContext } from '@src/core/socket';
import { useConfirm } from '@src/core/controllers/confirm-controller/hooks';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { useFlightProvider } from '@src/features/order/provider';
import {
  clearActiveGoHere,
  shouldClearGoHereOnEmergencyCommand,
} from '@src/features/drone/utils/go-here-registry';

const EnRouteEmergencyButtons: React.FC<EnRouteEmergencyButtonsProps> = (props) => {
  const { drone } = props;
  const { missionId } = useParams();
  const { flightData } = useFlightProvider();
  const { arcSocket } = React.useContext(ArcSocketContext);
  const [ sendCommand, { isLoading } ] = useSendCommandMutation();

  const dismissGoHere = React.useCallback(() => {
    clearActiveGoHere(drone?.serialNumber);
  }, [drone?.serialNumber]);
  const { confirm } = useConfirm();

  const onSend = async (command: EmergencyStatusEnum) => {
    // Show confirmation for LAND command
    if (command === EmergencyStatusEnum.LAND) {
      const confirmed = await confirm(
        'The drone will descend and land at its current location.',
        'Confirm Land?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    // Show confirmation for RETURN command
    if (command === EmergencyStatusEnum.RETURN) {
      const confirmed = await confirm(
        'The drone will stop its mission and fly back to the return point.',
        'Confirm Return?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    // Show confirmation for PAUSE command
    if (command === EmergencyStatusEnum.PAUSE) {
      const confirmed = await confirm(
        'The drone will stop and hover in place until further command.',
        'Confirm Pause?'
      );
      if (!confirmed) {
        return;
      }
    }

    if (command === EmergencyStatusEnum.CONTINUE) {
      const confirmed = await confirm(
        'The drone will continue its flight from the current position.',
        'Continue Flight?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    if (shouldClearGoHereOnEmergencyCommand(command)) {
      dismissGoHere();
    }
    sendCommand({ missionId, command }).then(() => {
      toast.success(`${command} SUCCESS TEXT`);
    });
  };

  const onEndMission = async () => {
    const confirmed = await confirm(
      'The drone will disarm, save flight data, and finish current mission. Ensure it has safely landed and the area around it is clear.',
      'Confirm End Mission?'
    );
    if (!confirmed) return;

    dismissGoHere();
    const socket = arcSocket();
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: 'COMMAND',
          command: 'END_MISSION',
          serialNumber: drone?.serialNumber,
        })
      );
    }
  };

  return ( 
    <ArcStack>
      <ArcStack 
        height={18}
        spacing='4px' 
        direction='row' 
        alignItems='center'
        sx={{ cursor: 'pointer' }}>
        <ArcText.caption fontSize={12} fontWeight={400}> EMERGENCY SIGNALS </ArcText.caption>
        <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
      </ArcStack>

      
      <ArcStack mt={1} spacing={1}>
        <ArcStack direction='row' spacing={1}>
          <ArcButton.ghost 
            fullWidth 
            onClick={() => onSend(EmergencyStatusEnum.LAND)}
            startIcon={<FlightTakeoffIcon />}> 
            LAND 
          </ArcButton.ghost>
          <ArcButton.ghost 
            fullWidth 
            isLoading={isLoading}
            startIcon={<ReplayRoundedIcon />}
            onClick={() => onSend(EmergencyStatusEnum.RETURN)}> 
            RETURN 
          </ArcButton.ghost>

          {flightData?.flightMode === 'HOLD' 
            ? <ArcButton.ghost 
              fullWidth 
              isLoading={isLoading}
              startIcon={<ContinueIcon />}
              onClick={() => onSend(EmergencyStatusEnum.CONTINUE)}> 
              CONTINUE 
            </ArcButton.ghost> 
            : <ArcButton.ghost 
              fullWidth 
              isLoading={isLoading}
              startIcon={<PauseIcon />}
              onClick={() => onSend(EmergencyStatusEnum.PAUSE)}> 
              PAUSE
            </ArcButton.ghost> }
          
        </ArcStack>
        <ArcStack>
          <ArcButton.ghost 
            isLoading={isLoading} 
            startIcon={<DoNotDisturbAltIcon />} 
            onClick={onEndMission}>
            END MISSION
          </ArcButton.ghost>
        </ArcStack>
      </ArcStack>
    </ArcStack>
  );
};

export default EnRouteEmergencyButtons;